/**
 * Australian states and territories
 */
export type AustralianState = "ACT" | "NSW" | "NT" | "QLD" | "SA" | "TAS" | "VIC" | "WA";

export const AUSTRALIAN_STATES: AustralianState[] = [
  "ACT", "NSW", "NT", "QLD", "SA", "TAS", "VIC", "WA"
];

export const STATE_NAMES: Record<AustralianState, string> = {
  ACT: "Australian Capital Territory",
  NSW: "New South Wales",
  NT: "Northern Territory",
  QLD: "Queensland",
  SA: "South Australia",
  TAS: "Tasmania",
  VIC: "Victoria",
  WA: "Western Australia",
};

/**
 * Property types
 */
export type PropertyType = "primary" | "investment";
export type PurchaseType = "established" | "new" | "vacant";

/**
 * Stamp duty rate tier
 */
export interface StampDutyTier {
  min: number;
  max: number | null; // null = no upper limit
  base: number;
  rate: number; // percentage as decimal (e.g., 0.04 = 4%)
}

/**
 * First Home Buyer concession details
 */
export interface FHBConcession {
  exemptionThreshold: number; // Full exemption up to this value
  concessionThreshold: number; // Partial concession up to this value
  maxConcession?: number; // Maximum concession amount
}

/**
 * First Home Owner Grant details
 */
export interface FHOGDetails {
  amount: number;
  newHomeOnly: boolean;
  maxPropertyValue: number;
  regionalBonus?: number;
}

/**
 * State-specific stamp duty configuration
 */
export interface StateStampDutyConfig {
  rates: StampDutyTier[];
  investmentRates?: StampDutyTier[]; // Some states have different rates
  foreignBuyerSurcharge: number; // percentage as decimal
  fhbConcession: FHBConcession;
  fhog: FHOGDetails;
  mortgageRegistrationFee: number;
  transferFeeBase: number;
  transferFeePerThousand: number;
  pensionerConcession?: {
    maxPropertyValue: number;
    concessionPercent: number;
  };
}

/**
 * Calculator inputs
 */
export interface StampDutyInputs {
  state: AustralianState;
  propertyValue: number;
  propertyType: PropertyType;
  purchaseType: PurchaseType;
  isFirstHomeBuyer: boolean;
  isPensioner: boolean;
  totalIncome: number;
  dependentChildren: number;
}

/**
 * Calculator results
 */
export interface StampDutyResults {
  stampDuty: number;
  mortgageRegistration: number;
  transferFee: number;
  totalGovernmentFees: number;
  fhog: number;
  fhbConcession: number;
  pensionerConcession: number;
  totalGovernmentGrant: number;
  netCost: number;
}
